/**
 * Comparison Export Utilities
 *
 * Generates Excel and PDF exports for scenario comparisons
 */
import * as XLSX from 'xlsx';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import React from 'react';

/**
 * Format currency for display
 */
function formatCurrency(value) {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
    }).format(value);
}

/**
 * Get the most recent optimization run for a scenario
 */
function getLatestRun(scenario) {
    const runs = scenario.optimization_runs || [];
    if (runs.length === 0) return null;

    const lastRunUuid = scenario.last_run_uuid;
    if (lastRunUuid) {
        const matchingRun = runs.find(r => r.id === lastRunUuid || r.nrel_run_uuid === lastRunUuid);
        if (matchingRun) return matchingRun;
    }

    const completedRuns = runs.filter(r => ['completed', 'optimal', 'Optimal Obtained'].includes(r.status));
    if (completedRuns.length > 0) {
        return completedRuns.sort((a, b) => {
            const aTime = a.completed_at ? new Date(a.completed_at) : new Date(0);
            const bTime = b.completed_at ? new Date(b.completed_at) : new Date(0);
            return bTime - aTime;
        })[0];
    }

    return runs[0];
}

/**
 * Get outputs from last_run_results or reopt_results
 */
function getOutputs(scenario) {
    const results = scenario.last_run_results || scenario.reopt_results || {};
    const outputs = results.outputs || {};
    return outputs.Scenario?.Site || outputs;
}

/**
 * Extract financial data from scenario
 * Matches the logic in ScenarioCompareModalV2.js
 */
function getFinancialData(scenario) {
    // Primary: ui_state.financialSummary (post-processed data)
    const savedSummary = scenario.ui_state?.financialSummary;
    if (savedSummary && (savedSummary.npv !== undefined || savedSummary.capex_breakdown)) {
        return {
            npv: savedSummary.npv ?? null,
            paybackPeriod: savedSummary.paybackPeriod ?? null,
            totalCapex: savedSummary.capex_breakdown?.net_capex ?? savedSummary.capex_breakdown?.total ?? savedSummary.initialInvestment ?? null,
            annualSavings: savedSummary.annualSavings ?? null,
            year1NetSavings: savedSummary.year1NetSavings ?? null,
            lifecycleSavings: savedSummary.totalSavings ?? null,
        };
    }

    // No saved financial summary - return nulls
    return {
        npv: null,
        paybackPeriod: null,
        totalCapex: null,
        annualSavings: null,
        year1NetSavings: null,
        lifecycleSavings: null,
    };
}

/**
 * Extract system configuration from scenario
 * Matches the logic in ScenarioCompareModalV2.js
 */
function getSystemConfig(scenario) {
    const existingSolarKw = parseFloat(scenario.ui_state?.loadProfile?.existingSolarParams?.system_capacity) || 0;
    const latestRun = getLatestRun(scenario);

    // Primary: solver-calculated values from optimization run
    if (latestRun) {
        return {
            pvKw: latestRun.pv_kw ?? 0,
            existingSolarKw,
            storageKw: latestRun.storage_kw ?? 0,
            storageKwh: latestRun.storage_kwh ?? 0,
            generatorKw: latestRun.generator_kw ?? 0,
            tariff: scenario.tariff_data?.name || scenario.tariff_label || 'N/A',
        };
    }

    // Fallback: REopt outputs
    const outputs = getOutputs(scenario);
    return {
        pvKw: outputs.PV?.size_kw ?? 0,
        existingSolarKw,
        storageKw: outputs.ElectricStorage?.size_kw ?? 0,
        storageKwh: outputs.ElectricStorage?.size_kwh ?? 0,
        generatorKw: outputs.Generator?.size_kw ?? 0,
        tariff: scenario.tariff_data?.name || scenario.tariff_label || 'N/A',
    };
}

/**
 * Extract cash flows from scenario
 * Matches the logic in ScenarioCompareModalV2.js
 */
function getCashFlows(scenario) {
    // Primary: ui_state.financialSummary.cash_flows
    const savedCashFlows = scenario.ui_state?.financialSummary?.cash_flows;
    return savedCashFlows && savedCashFlows.length > 0 ? savedCashFlows : [];
}

/**
 * Extract CapEx breakdown from scenario
 * Matches the logic in ScenarioCompareModalV2.js
 */
function getCapexBreakdown(scenario) {
    // Primary: ui_state.financialSummary.capex_breakdown
    const savedCapex = scenario.ui_state?.financialSummary?.capex_breakdown;
    if (savedCapex) {
        return {
            solar: savedCapex.solar ?? null,
            storage: savedCapex.storage ?? null,
            generator: savedCapex.generator ?? null,
            bos: savedCapex.bos ?? null,
            installation: null,
            total: savedCapex.total ?? null,
            netCapex: savedCapex.net_capex ?? null,
        };
    }

    return {
        solar: null,
        storage: null,
        generator: null,
        bos: null,
        installation: null,
        total: null,
        netCapex: null,
    };
}

/**
 * Generate Investment Model Excel export
 */
export function exportInvestmentModel(scenarios, projectName) {
    const wb = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
        ['Scenario Comparison - Investment Model'],
        ['Project:', projectName],
        ['Generated:', new Date().toLocaleDateString()],
        [],
        ['Metric', ...scenarios.map(s => s.name)],
        ['NPV', ...scenarios.map(s => getFinancialData(s).npv)],
        ['Payback Period (years)', ...scenarios.map(s => getFinancialData(s).paybackPeriod)],
        ['Total CapEx', ...scenarios.map(s => getFinancialData(s).totalCapex)],
        ['Annual Savings (Year 1)', ...scenarios.map(s => getFinancialData(s).annualSavings)],
        ['Lifecycle Savings', ...scenarios.map(s => getFinancialData(s).lifecycleSavings)],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // CapEx Breakdown Sheet
    const capexData = [
        ['CapEx Breakdown'],
        [],
        ['Component', ...scenarios.map(s => s.name)],
        ['Solar PV', ...scenarios.map(s => getCapexBreakdown(s).solar)],
        ['Battery Storage', ...scenarios.map(s => getCapexBreakdown(s).storage)],
        ['Generator', ...scenarios.map(s => getCapexBreakdown(s).generator)],
        ['Balance of System', ...scenarios.map(s => getCapexBreakdown(s).bos)],
        ['Total', ...scenarios.map(s => getCapexBreakdown(s).total)],
    ];
    const capexSheet = XLSX.utils.aoa_to_sheet(capexData);
    XLSX.utils.book_append_sheet(wb, capexSheet, 'CapEx Breakdown');

    // Cash Flows Sheet
    const maxYears = 25;
    const cashFlowData = [
        ['Cash Flows'],
        [],
        ['Year', ...scenarios.map(s => s.name)],
    ];
    for (let year = 0; year <= maxYears; year++) {
        const row = [year];
        scenarios.forEach(s => {
            const flows = getCashFlows(s);
            const flow = flows.find(f => f.year === year);
            row.push(flow ? (flow.netCashflow ?? flow.net_cashflow) : null);
        });
        cashFlowData.push(row);
    }
    const cashFlowSheet = XLSX.utils.aoa_to_sheet(cashFlowData);
    XLSX.utils.book_append_sheet(wb, cashFlowSheet, 'Cash Flows');

    // Assumptions Sheet
    const assumptions = scenarios[0]?._vortx_analysis?.assumptions || {};
    const assumptionsData = [
        ['Financial Assumptions'],
        [],
        ['Parameter', 'Value'],
        ['Analysis Period (years)', assumptions.analysisPeriod ?? 25],
        ['Discount Rate', assumptions.discountRate ?? 0.05],
        ['Electricity Escalation Rate', assumptions.electricityEscalation ?? 0.02],
        ['O&M Escalation Rate', assumptions.omEscalation ?? 0.02],
    ];
    const assumptionsSheet = XLSX.utils.aoa_to_sheet(assumptionsData);
    XLSX.utils.book_append_sheet(wb, assumptionsSheet, 'Assumptions');

    // Download
    XLSX.writeFile(wb, `${projectName}_Investment_Model.xlsx`);
}

/**
 * Generate Full Data Excel export
 */
export function exportFullData(scenarios, projectName) {
    const wb = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
        ['Scenario Comparison - Full Data Export'],
        ['Project:', projectName],
        ['Generated:', new Date().toLocaleDateString()],
        [],
        ['Metric', ...scenarios.map(s => s.name)],
        ['NPV', ...scenarios.map(s => getFinancialData(s).npv)],
        ['Payback Period (years)', ...scenarios.map(s => getFinancialData(s).paybackPeriod)],
        ['Total CapEx', ...scenarios.map(s => getFinancialData(s).totalCapex)],
        ['Annual Savings (Year 1)', ...scenarios.map(s => getFinancialData(s).annualSavings)],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // System Configuration Sheet
    const configData = [
        ['System Configuration'],
        [],
        ['Parameter', ...scenarios.map(s => s.name)],
        ['Solar PV (kW)', ...scenarios.map(s => getSystemConfig(s).pvKw)],
        ['Battery Power (kW)', ...scenarios.map(s => getSystemConfig(s).storageKw)],
        ['Battery Capacity (kWh)', ...scenarios.map(s => getSystemConfig(s).storageKwh)],
        ['Generator (kW)', ...scenarios.map(s => getSystemConfig(s).generatorKw)],
        ['Tariff', ...scenarios.map(s => getSystemConfig(s).tariff)],
    ];
    const configSheet = XLSX.utils.aoa_to_sheet(configData);
    XLSX.utils.book_append_sheet(wb, configSheet, 'System Configuration');

    // CapEx Breakdown Sheet
    const capexData = [
        ['CapEx Breakdown'],
        [],
        ['Component', ...scenarios.map(s => s.name)],
        ['Solar PV', ...scenarios.map(s => getCapexBreakdown(s).solar)],
        ['Battery Storage', ...scenarios.map(s => getCapexBreakdown(s).storage)],
        ['Generator', ...scenarios.map(s => getCapexBreakdown(s).generator)],
        ['Total', ...scenarios.map(s => getCapexBreakdown(s).total)],
    ];
    const capexSheet = XLSX.utils.aoa_to_sheet(capexData);
    XLSX.utils.book_append_sheet(wb, capexSheet, 'CapEx Breakdown');

    // Cash Flows Sheet
    const maxYears = 25;
    const cashFlowData = [['Year', ...scenarios.map(s => s.name)]];
    for (let year = 0; year <= maxYears; year++) {
        const row = [year];
        scenarios.forEach(s => {
            const flows = getCashFlows(s);
            const flow = flows.find(f => f.year === year);
            row.push(flow ? (flow.netCashflow ?? flow.net_cashflow) : null);
        });
        cashFlowData.push(row);
    }
    const cashFlowSheet = XLSX.utils.aoa_to_sheet(cashFlowData);
    XLSX.utils.book_append_sheet(wb, cashFlowSheet, 'Cash Flows');

    // Download
    XLSX.writeFile(wb, `${projectName}_Full_Data.xlsx`);
}

// PDF Styles
const pdfStyles = StyleSheet.create({
    page: { padding: 40, fontFamily: 'Helvetica' },
    header: { marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 'bold', color: '#B28C50', marginBottom: 5 },
    subtitle: { fontSize: 12, color: '#666', marginBottom: 20 },
    sectionTitle: { fontSize: 16, fontWeight: 'bold', marginTop: 20, marginBottom: 10 },
    table: { display: 'flex', width: '100%', marginBottom: 15 },
    tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eee' },
    tableHeaderRow: { flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#B28C50', backgroundColor: '#f5f5f5' },
    tableCell: { flex: 1, padding: 8, fontSize: 10 },
    tableCellHeader: { flex: 1, padding: 8, fontSize: 10, fontWeight: 'bold' },
    recommendation: { marginTop: 20, padding: 15, backgroundColor: '#f0f9f4', borderRadius: 4 },
    recommendationTitle: { fontSize: 14, fontWeight: 'bold', color: '#22c55e', marginBottom: 5 },
    recommendationText: { fontSize: 11, color: '#333' },
    footer: { position: 'absolute', bottom: 30, left: 40, right: 40, fontSize: 9, color: '#999', textAlign: 'center' },
});

/**
 * Generate PDF presentation
 */
export async function exportPresentation(scenarios, projectName) {
    // Debug: log what data we're receiving
    console.log('[ExportDebug] exportPresentation called with scenarios:', scenarios.map(s => ({
        name: s.name,
        id: s.id,
        has_ui_state: !!s.ui_state,
        has_financialSummary: !!s.ui_state?.financialSummary,
        financialSummary: s.ui_state?.financialSummary,
        has_optimization_runs: !!(s.optimization_runs?.length),
        optimization_runs_count: s.optimization_runs?.length || 0,
    })));

    // Find best scenario by NPV
    let bestScenario = scenarios[0];
    let bestNpv = getFinancialData(scenarios[0]).npv || 0;
    scenarios.forEach(s => {
        const npv = getFinancialData(s).npv || 0;
        if (npv > bestNpv) {
            bestNpv = npv;
            bestScenario = s;
        }
    });

    const PresentationDoc = () => (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                {/* Header */}
                <View style={pdfStyles.header}>
                    <Text style={pdfStyles.title}>{projectName}</Text>
                    <Text style={pdfStyles.subtitle}>
                        Scenario Comparison Report | Generated {new Date().toLocaleDateString()}
                    </Text>
                </View>

                {/* Executive Summary */}
                <Text style={pdfStyles.sectionTitle}>Executive Summary</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeaderRow}>
                        <Text style={pdfStyles.tableCellHeader}>Scenario</Text>
                        <Text style={pdfStyles.tableCellHeader}>NPV</Text>
                        <Text style={pdfStyles.tableCellHeader}>Payback</Text>
                        <Text style={pdfStyles.tableCellHeader}>CapEx</Text>
                    </View>
                    {scenarios.map(s => {
                        const fin = getFinancialData(s);
                        return (
                            <View key={s.id} style={pdfStyles.tableRow}>
                                <Text style={pdfStyles.tableCell}>{s.name}</Text>
                                <Text style={pdfStyles.tableCell}>{formatCurrency(fin.npv)}</Text>
                                <Text style={pdfStyles.tableCell}>
                                    {fin.paybackPeriod ? `${fin.paybackPeriod.toFixed(1)} yrs` : '-'}
                                </Text>
                                <Text style={pdfStyles.tableCell}>{formatCurrency(fin.totalCapex)}</Text>
                            </View>
                        );
                    })}
                </View>

                {/* System Configuration */}
                <Text style={pdfStyles.sectionTitle}>System Configuration</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeaderRow}>
                        <Text style={pdfStyles.tableCellHeader}>Scenario</Text>
                        <Text style={pdfStyles.tableCellHeader}>Solar (kW)</Text>
                        <Text style={pdfStyles.tableCellHeader}>Battery (kW/kWh)</Text>
                        <Text style={pdfStyles.tableCellHeader}>Generator (kW)</Text>
                    </View>
                    {scenarios.map(s => {
                        const cfg = getSystemConfig(s);
                        return (
                            <View key={s.id} style={pdfStyles.tableRow}>
                                <Text style={pdfStyles.tableCell}>{s.name}</Text>
                                <Text style={pdfStyles.tableCell}>{cfg.pvKw.toFixed(0)}</Text>
                                <Text style={pdfStyles.tableCell}>
                                    {cfg.storageKw.toFixed(0)} / {cfg.storageKwh.toFixed(0)}
                                </Text>
                                <Text style={pdfStyles.tableCell}>
                                    {cfg.generatorKw > 0 ? cfg.generatorKw.toFixed(0) : '-'}
                                </Text>
                            </View>
                        );
                    })}
                </View>

                {/* Recommendation */}
                <View style={pdfStyles.recommendation}>
                    <Text style={pdfStyles.recommendationTitle}>Recommendation</Text>
                    <Text style={pdfStyles.recommendationText}>
                        Based on NPV analysis, {bestScenario.name} offers the highest return with an NPV of {formatCurrency(bestNpv)}.
                    </Text>
                </View>

                {/* Footer */}
                <Text style={pdfStyles.footer}>
                    Generated by Vortx Flux | vortx.us
                </Text>
            </Page>
        </Document>
    );

    // Generate and download
    const blob = await pdf(<PresentationDoc />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectName}_Presentation.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
